import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { DrawingManagerF } from '@react-google-maps/api';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';

import IconButton from 'src/components/ui/IconButton';
import Button from 'src/components/ui/Button';

import { styledComponent } from 'src/utils/styled';
import { colours } from 'src/constants/colours';
import Icon from 'src/components/ui/Icon';
import { breakpoints } from 'src/constants/breakpoints';

const GoogleMapDrawingOptionContainer = styledComponent(Box)<{ $isDrawingMode: boolean }>`
    width: fit-content;
    align-items: center;
    position: relative;
    top: 0.625rem; /* 10px margin left like other control */
    left: 0.625rem; /* 10px margin left like other control */
    display: flex;
    ${({ $isDrawingMode }) =>
        $isDrawingMode &&
        `
        @media (max-width: ${breakpoints.breakPoint450}px) {
            left: 50%;
            transform: translateX(-50%);
        }
    `}
`;

const StyledEditContainer = styledComponent(Box)`
    max-width: min(24rem, 90vw);
    display: flex;
    flex-direction: column;
    border-radius: 0.25rem;
    margin-top: 0.75rem;
    padding: 1.2rem;
    justify-content: space-between;
    gap: 1rem;
    align-items: start;
    background-color: ${colours.white.main};
    @media (min-width: ${breakpoints.breakPoint450 + 1}px) {
        margin-left: 0.75rem;
    }
`;

const StyledEditContainerButtonRow = styledComponent(Box)`
    display: flex;
    gap: 1rem;
    width: 100%;
    button {
        padding: 0.38rem 1rem;
        height: 2rem;
        flex: 1;
    }
`;

const ErrorContainer = styledComponent(Box)`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 1rem;
    background-color: ${colours.redErrorBackground};
    border-radius: 0.5rem;
`;

const drawPolygonDefaultOptions = {
    editable: true,
    strokeColor: colours.secondary.main,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: colours.secondary.main,
    fillOpacity: 0,
};

type Props = {
    isDrawingMode: boolean;
    hasDrawnPolygon: boolean;
    isDrawnPolygonValid: boolean;
    drawingError?: string;
    onClearClick: () => void;
    onSearchClick: () => void;
    onDrawClick: () => void;
    onDeleteZoneClick: () => void;
    onPolygonComplete: (polygon: google.maps.Polygon) => void;
    onDrawingManagerLoad: (drawingManager: google.maps.drawing.DrawingManager) => void;
};

export const GoogleMapDrawingControlOption: React.FC<Props> = ({
    isDrawingMode,
    hasDrawnPolygon,
    isDrawnPolygonValid,
    drawingError,
    onClearClick,
    onSearchClick,
    onDrawClick,
    onDeleteZoneClick,
    onPolygonComplete,
    onDrawingManagerLoad,
}) => (
    <GoogleMapDrawingOptionContainer $isDrawingMode={isDrawingMode}>
        {isDrawingMode ? (
            <DrawingModeOption
                hasDrawnPolygon={hasDrawnPolygon}
                isDrawnPolygonValid={isDrawnPolygonValid}
                drawingError={drawingError}
                onClearClick={onClearClick}
                onSearchClick={onSearchClick}
            />
        ) : (
            <DrawingOption
                hasDrawnPolygon={hasDrawnPolygon}
                onDrawClick={onDrawClick}
                onDeleteZoneClick={onDeleteZoneClick}
            />
        )}
        <DrawingManagerF
            options={{
                drawingControl: false,
                polygonOptions: drawPolygonDefaultOptions,
            }}
            onPolygonComplete={onPolygonComplete}
            onLoad={onDrawingManagerLoad}
        />
    </GoogleMapDrawingOptionContainer>
);

type DrawingModeOptionProps = {
    hasDrawnPolygon: boolean;
    isDrawnPolygonValid: boolean;
    drawingError?: string;
    onClearClick: () => void;
    onSearchClick: () => void;
};

const DrawingModeOption: React.FC<DrawingModeOptionProps> = ({
    hasDrawnPolygon,
    isDrawnPolygonValid,
    drawingError,
    onClearClick,
    onSearchClick,
}) => {
    const { t } = useTranslation(['common', 'ad']);

    const modalTutorialText = useMemo(() => {
        if (hasDrawnPolygon) {
            return t('ad:mapDrawing.modalTutorialTextSearch');
        } else {
            return t('ad:mapDrawing.modalTutorialTextCreate');
        }
    }, [hasDrawnPolygon, t]);

    return (
        <StyledEditContainer>
            <Box display="flex" flexDirection="column">
                <Typography variant="h5" fontWeight={400} maxWidth="21rem">
                    {modalTutorialText}
                </Typography>
                {!isDrawnPolygonValid && drawingError && (
                    <ErrorContainer>
                        <Icon name="Warning" color={colours.redError} size="1.5rem" />
                        <Typography variant="h5" color="error" fontWeight={400}>
                            {drawingError}
                        </Typography>
                    </ErrorContainer>
                )}
            </Box>
            <StyledEditContainerButtonRow>
                <Button variant="outlined" onClick={onClearClick}>
                    {t('ad:mapDrawing.erase')}
                </Button>
                <Button onClick={onSearchClick} disabled={!isDrawnPolygonValid}>
                    {t('common:search')}
                </Button>
            </StyledEditContainerButtonRow>
        </StyledEditContainer>
    );
};

type DrawingOption = {
    hasDrawnPolygon: boolean;
    onDrawClick: () => void;
    onDeleteZoneClick: () => void;
};

const DrawingOption: React.FC<DrawingOption> = ({ hasDrawnPolygon, onDrawClick, onDeleteZoneClick }) => {
    const { t } = useTranslation(['ad']);
    return (
        <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton
                color="white"
                onClick={onDrawClick}
                iconProps={{
                    name: 'Draw',
                    size: '1.5rem',
                }}
                sx={{
                    borderRadius: '0.25rem',
                }}
            />
            {hasDrawnPolygon && (
                <Box marginLeft="1rem" borderRadius="0.25rem" style={{ backgroundColor: colours.white.main }}>
                    <Button
                        color="white"
                        variant="outlined"
                        size="medium"
                        sx={{
                            padding: '0.4rem 1rem',
                            border: 'none',
                        }}
                        onClick={onDeleteZoneClick}
                    >
                        {t('ad:mapDrawing.deleteZone')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
